import { db, storage } from '../firebase/config';
import { doc, updateDoc, getDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export interface QRDesignData {
  qrColor: string;
  qrBackgroundColor: string;
  qrLogoUrl?: string;
  qrImageUrl?: string;
  qrValue: string;
  createdAt: any;
}

// 로고 업로드 최대 크기 (Storage 규칙과 동일하게 5MB)
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
// QR 로고는 작게 표시되므로 512px면 충분하다 (용량 절감)
const LOGO_MAX_DIMENSION = 512;

export class QRService {
  /**
   * QR 로고 이미지를 Firebase Storage에 업로드하고 다운로드 URL을 반환한다.
   *
   * ⚠️ base64 data URL을 Firestore 문서에 직접 저장하면 안 된다.
   * Firestore 문서 크기 한도(1MB)를 쉽게 넘겨 저장이 실패한다.
   */
  static async uploadQRLogo(userId: string, cardId: string, file: File): Promise<string> {
    if (!file.type.startsWith('image/')) {
      throw new Error('이미지 파일만 업로드할 수 있습니다.');
    }
    if (file.size > MAX_LOGO_BYTES) {
      throw new Error('로고 이미지는 5MB 이하만 업로드할 수 있습니다.');
    }

    // 업로드 전 리사이즈 (용량 및 렌더링 최적화)
    const blob = await resizeImage(file, LOGO_MAX_DIMENSION);

    // storage.rules 의 users/{userId}/qr-logos/{logoId} 경로 사용
    const path = `users/${userId}/qr-logos/${cardId}-${Date.now()}.png`;
    const storageRef = ref(storage, path);

    const snapshot = await uploadBytes(storageRef, blob, { contentType: 'image/png' });
    return await getDownloadURL(snapshot.ref);
  }

  static async updateQRDesign(userId: string, cardId: string, designData: QRDesignData): Promise<void> {
    try {
      const cardRef = doc(db, 'users', userId, 'cards', cardId);

      const logoUrl = designData.qrLogoUrl;

      // ⚠️ Firestore는 undefined 값을 거부한다. 로고가 없으면 필드를 삭제한다.
      const logoValue = logoUrl ? logoUrl : deleteField();

      // qrDesign 객체에는 undefined 필드가 들어가지 않도록 조립
      const qrDesign: Record<string, any> = {
        qrColor: designData.qrColor,
        qrBackgroundColor: designData.qrBackgroundColor,
        qrValue: designData.qrValue,
        createdAt: designData.createdAt ?? new Date(),
      };
      if (logoUrl) {
        qrDesign.qrLogoUrl = logoUrl;
      }
      if (designData.qrImageUrl) {
        qrDesign.qrImageUrl = designData.qrImageUrl;
      }

      // QR 디자인을 두 가지 형태로 저장
      // 1. qrDesign 객체 안에 저장 (웹에서 사용)
      // 2. 최상위 레벨에 저장 (Firebase Function에서 사용)
      await updateDoc(cardRef, {
        qrDesign,
        // Firebase Function 호환성을 위한 최상위 레벨 저장
        qrColor: designData.qrColor,
        qrBackgroundColor: designData.qrBackgroundColor,
        qrValue: designData.qrValue,
        qrLogoUrl: logoValue,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('QR design update failed:', error);
      throw error;
    }
  }

  static async getQRDesign(userId: string, cardId: string): Promise<QRDesignData | null> {
    try {
      const cardRef = doc(db, 'users', userId, 'cards', cardId);
      const cardDoc = await getDoc(cardRef);

      if (cardDoc.exists()) {
        const data = cardDoc.data();
        // qrDesign 객체가 있으면 사용, 없으면 최상위 레벨 데이터 사용
        if (data.qrDesign) {
          return data.qrDesign;
        } else if (data.qrColor || data.qrBackgroundColor || data.qrLogoUrl) {
          // 최상위 레벨 데이터가 있으면 qrDesign 형태로 변환
          return {
            qrColor: data.qrColor || '#000000',
            qrBackgroundColor: data.qrBackgroundColor || '#FFFFFF',
            qrLogoUrl: data.qrLogoUrl,
            qrValue: data.qrValue || '',
            createdAt: data.createdAt || new Date()
          };
        }
      }
      return null;
    } catch (error) {
      console.error('QR design fetch failed:', error);
      throw error;
    }
  }

  // 기존 QR 디자인 데이터를 마이그레이션하는 함수
  static async migrateQRDesignData(userId: string, cardId: string): Promise<void> {
    try {
      const cardRef = doc(db, 'users', userId, 'cards', cardId);
      const cardDoc = await getDoc(cardRef);

      if (cardDoc.exists()) {
        const data = cardDoc.data();

        // qrDesign 객체가 없지만 최상위 레벨에 QR 데이터가 있는 경우
        if (!data.qrDesign && (data.qrColor || data.qrBackgroundColor || data.qrLogoUrl)) {
          const qrDesignData: Record<string, any> = {
            qrColor: data.qrColor || '#000000',
            qrBackgroundColor: data.qrBackgroundColor || '#FFFFFF',
            qrValue: data.qrValue || '',
            createdAt: data.createdAt || new Date()
          };
          // undefined 필드가 들어가지 않도록 존재할 때만 추가
          if (data.qrLogoUrl) {
            qrDesignData.qrLogoUrl = data.qrLogoUrl;
          }

          // qrDesign 객체로 마이그레이션
          await updateDoc(cardRef, {
            qrDesign: qrDesignData,
            updatedAt: serverTimestamp()
          });

          console.log('QR design data migrated for card:', cardId);
        }
      }
    } catch (error) {
      console.error('QR design migration failed:', error);
      // 마이그레이션 실패가 화면 로딩을 막지 않도록 삼킨다
    }
  }
}

/**
 * 이미지를 지정한 최대 변 길이 이하로 리사이즈해 PNG Blob으로 반환한다.
 * 리사이즈에 실패하면 원본 파일을 그대로 반환한다.
 */
async function resizeImage(file: File, maxDimension: number): Promise<Blob> {
  try {
    const dataUrl = await readFileAsDataURL(file);
    const image = await loadImage(dataUrl);

    const largestSide = Math.max(image.width, image.height);
    // 이미 충분히 작으면 원본 유지
    if (largestSide <= maxDimension) {
      return file;
    }

    const scale = maxDimension / largestSide;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });

    return blob || file;
  } catch (error) {
    console.warn('로고 리사이즈 실패, 원본으로 업로드합니다:', error);
    return file;
  }
}

function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('이미지를 불러올 수 없습니다.'));
    image.src = src;
  });
}
